import { ConditionEnum } from '@models';

export interface ClerkData {
  data: {
    clerk?: {
      response?: {
        client_id?: string
      }
    }
  }
}

export interface ClerkInitPayload {
  userId?: number,
  abTests: string,
}

export type ClerkPageViewEventPayload = Record<string, boolean | number | string>;

export interface ClerkEventPayload {
  category: ClerkCategoryType,
  action: ClerkActionType,
  payload?: Record<string, boolean | number | string | undefined | null>,
}

export enum ClerkActionType {
  click = 'click',
  scroll = 'scroll',
  show = 'show',
}

export enum ClerkCategoryType {
  newDialog = 'new_dialog',
  buyButton = 'buy_button',
  addToFavourite = 'add_to_favourite',
  goToDetail = 'go_to_detail',
  suggestionBlock = 'suggestion_block',
  topSellerProducts = 'business_sellers-index',
  newArrivalsBlock = 'new_arrivals_block',
  dayDiscountBlock = 'day_discount_block',
  purchase = 'purchase',
  viewItem = 'view_item',
  itemAdded = 'item_added',
  socialFeed = 'social_feed',
  socialFeedPost = 'social_feed_post',
  checkout = 'checkout',
}

export enum ClerkSourceType {
  personalSuggestionsBlock = 'personalSuggestionsBlock',
  personalTopSellerProductsBlock = 'business_sellers-index',
  personalNewArrivalsBlock = 'personalCollectionBlock',
  personalDayDiscountBlock = 'personalDayDiscountBlock',
}

export interface ClerkAddToFavouriteEvent extends ClerkEventPayload {
  category: ClerkCategoryType.addToFavourite,
  action: ClerkActionType.click,
  payload: {
    itemCount: string,
    referrer?: string,
  }
}

export interface ClerkShowSuggestionBlockEvent extends ClerkEventPayload {
  category: ClerkCategoryType.suggestionBlock,
  action: ClerkActionType.show,
  payload: {
    source: ClerkSourceType.personalSuggestionsBlock,
  }
}

export interface ClerkGetMoreSuggestionsEvent extends ClerkEventPayload {
  category: ClerkCategoryType.suggestionBlock,
  action: ClerkActionType.scroll,
  payload: {
    source: ClerkSourceType.personalSuggestionsBlock,
  }
}

export interface ClerkShowTopSellerProductsBlockEvent extends ClerkEventPayload {
  category: ClerkCategoryType.topSellerProducts,
  action: ClerkActionType.show,
  payload: {
    source: ClerkSourceType.personalTopSellerProductsBlock,
  }
}

export interface ClerkShowNewArrivalsBlockEvent extends ClerkEventPayload {
  category: ClerkCategoryType.newArrivalsBlock,
  action: ClerkActionType.show,
  payload: {
    source: ClerkSourceType.personalNewArrivalsBlock,
  }
}

export interface ClerkShowDayDiscountBlockEvent extends ClerkEventPayload {
  category: ClerkCategoryType.dayDiscountBlock,
  action: ClerkActionType.show,
  payload: {
    source: ClerkSourceType.personalDayDiscountBlock,
  }
}

export interface ClerkNewDialogEvent extends ClerkEventPayload {
  category: ClerkCategoryType.newDialog,
  action: ClerkActionType.click,
}

export interface ClerkAddToFavoritesEvent extends ClerkEventPayload {
  category: ClerkCategoryType.addToFavourite,
  action: ClerkActionType.click,
}

// Responsible: @a.illiustrova, @a.melon
export interface ClerkPurchaseEvent extends ClerkEventPayload {
  category: ClerkCategoryType.purchase,
  action: ClerkActionType.show,
  payload: {
    productId: number,
    source: 'chat' | 'order',
    sellerOrderRate: string,
    threadId: number,
    isNewBuyer: boolean,
    inPriceRange?: boolean,
    referrer?: string,
  },
}

// Responsible: @a.illiustrova, @a.melon
export interface ClerkViewItemEvent extends ClerkEventPayload {
  category: ClerkCategoryType.viewItem,
  action: ClerkActionType.show,
  payload: {
    productId: number,
    sellerOrderRate: string,
    product_scale: string,
    inPriceRange?: boolean,
    referrer?: string,
  },
}

// Responsible: @a.illiustrova
export interface ClerkSocialFeedOpenEvent extends ClerkEventPayload {
  category: ClerkCategoryType.socialFeed,
  action: ClerkActionType.show,
}

// Responsible: @a.illiustrova
export interface ClerkSocialFeedLoadMoreEvent extends ClerkEventPayload {
  category: ClerkCategoryType.socialFeed,
  action: ClerkActionType.scroll,
}

// Responsible: @a.illiustrova
export interface ClerkSocialFeedOpenPostEvent extends ClerkEventPayload {
  category: ClerkCategoryType.socialFeedPost,
  action: ClerkActionType.show,
}

// Responsible: @a.illiustrova
export interface ClerkSocialFeedLoadMorePostEvent extends ClerkEventPayload {
  category: ClerkCategoryType.socialFeedPost,
  action: ClerkActionType.scroll,
}

// Responsible: @a.illiustrova
export interface ClerkProductAddedEvent extends ClerkEventPayload {
  category: ClerkCategoryType.itemAdded,
  action: ClerkActionType.show,
  payload: {
    productId: number,
    product_scale: string,
    condition: ConditionEnum,
    title_count: number,
    description_count: number,
    photo_count: number,
    price?: number | null,
    recommendedPrice?: number | null,
    brand?: string,
  }
}

// Responsible: @a.melon
export interface ClerkCheckoutOpenEvent extends ClerkEventPayload {
  category: ClerkCategoryType.checkout,
  action: ClerkActionType.show,
  payload: {
    isBuyerHasEmail: boolean,
  }
}

// Responsible: @a.illiustrova
export interface ClerkProductClickEvent extends ClerkEventPayload {
  category: ClerkCategoryType.goToDetail,
  action: ClerkActionType.click,
  payload: {
    itemCount: number,
    productId: number,
    source?: 'catalog'
    | 'dynamicCollection'
    | 'personalSuggestions'
    | 'personalNewArrivals'
    | 'personalDiscount'
    | 'personalTopSeller'
  }
}
